import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Pagination from "@mui/material/Pagination";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { formatDateTime, getUserDisplayName } from "@bw/shared/utils";

import { PlatformPage } from "@/components/PlatformPage";

import { queryAuditLog } from "../api";
import { resourceHref } from "@/shared/resourceHref";

const PAGE_SIZE = 25;

function toRangeStart(date: string): string {
  return new Date(`${date}T00:00:00`).toISOString();
}

function toRangeEnd(date: string): string {
  return new Date(`${date}T23:59:59.999`).toISOString();
}

function escapeCsv(value: string): string {
  const needsFormulaNeutralization = /^\s*[=+\-@\t\r]/.test(value);
  const safeValue = needsFormulaNeutralization ? `'${value}` : value;
  return `"${safeValue.replace(/"/g, '""')}"`;
}

function formatMetadataLabel(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatMetadataValue(value: unknown): string {
  if (value == null) return "—";
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }
  return JSON.stringify(value);
}

function paginationLabel(type: string, page: number | null): string {
  if (type === "next") return "Next";
  if (type === "previous") return "Previous";
  if (type === "first") return "First";
  if (type === "last") return "Last";
  return page == null ? "More pages" : `Page ${page}`;
}

export function AuditLogPage() {
  const [actorFilter, setActorFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [resourceFilter, setResourceFilter] = useState("");
  const [resourceIdFilter, setResourceIdFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [debouncedActor, setDebouncedActor] = useState("");
  const [debouncedAction, setDebouncedAction] = useState("");
  const [debouncedResource, setDebouncedResource] = useState("");
  const [debouncedResourceId, setDebouncedResourceId] = useState("");
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedActor(actorFilter), 300);
    return () => clearTimeout(timer);
  }, [actorFilter]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedAction(actionFilter), 300);
    return () => clearTimeout(timer);
  }, [actionFilter]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedResource(resourceFilter), 300);
    return () => clearTimeout(timer);
  }, [resourceFilter]);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedResourceId(resourceIdFilter),
      300,
    );
    return () => clearTimeout(timer);
  }, [resourceIdFilter]);

  const since = fromDate ? toRangeStart(fromDate) : undefined;
  const until = toDate ? toRangeEnd(toDate) : undefined;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [
      "platform",
      "audit",
      debouncedActor,
      debouncedAction,
      debouncedResource,
      debouncedResourceId,
      since,
      until,
      offset,
    ],
    queryFn: () =>
      queryAuditLog({
        actor_id: debouncedActor || undefined,
        action: debouncedAction || undefined,
        resource_type: debouncedResource || undefined,
        resource_id: debouncedResourceId || undefined,
        since,
        until,
        limit: PAGE_SIZE,
        offset,
      }),
  });

  const exportMutation = useMutation({
    mutationFn: async () => {
      const entries = [];
      const seenEntryIds = new Set<string>();
      let nextOffset = 0;
      let authoritativeTotal: number | undefined;

      do {
        const page = await queryAuditLog({
          actor_id: debouncedActor || undefined,
          action: debouncedAction || undefined,
          resource_type: debouncedResource || undefined,
          resource_id: debouncedResourceId || undefined,
          since,
          until,
          limit: 500,
          offset: nextOffset,
        });

        if (authoritativeTotal === undefined) {
          authoritativeTotal = page.total;
        } else if (page.total !== authoritativeTotal) {
          throw new Error("Audit export total changed during pagination");
        }

        if (page.entries.length === 0 && entries.length < authoritativeTotal) {
          throw new Error("Audit export ended before the reported total");
        }

        for (const entry of page.entries) {
          if (seenEntryIds.has(entry.entry_id)) {
            throw new Error("Audit export contained a repeated entry");
          }
          seenEntryIds.add(entry.entry_id);
          entries.push(entry);
        }

        if (entries.length > authoritativeTotal) {
          throw new Error("Audit export exceeded the reported total");
        }
        nextOffset += page.entries.length;
      } while (entries.length < authoritativeTotal);

      return entries;
    },
    onSuccess: (entries) => {
      const header = [
        "timestamp",
        "actor",
        "actor_id",
        "action",
        "resource_type",
        "resource_id",
        "ip_address",
      ];
      const rows = entries.map((entry) => [
        entry.created_at,
        entry.actor_display_name || entry.actor_id,
        entry.actor_id,
        entry.action,
        entry.resource_type,
        entry.resource_id,
        entry.ip_address || "",
      ]);
      const csv = [header, ...rows]
        .map((row) => row.map((value) => escapeCsv(String(value))).join(","))
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "audit-log.csv";
      anchor.click();
      URL.revokeObjectURL(url);
    },
  });

  const entries = data?.entries ?? [];
  const total = data?.total ?? 0;
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const filtersDebouncing =
    actorFilter !== debouncedActor ||
    actionFilter !== debouncedAction ||
    resourceFilter !== debouncedResource ||
    resourceIdFilter !== debouncedResourceId;

  const controls = (
    <Button
      type="button"
      variant="outlined"
      size="small"
      disabled={exportMutation.isPending || filtersDebouncing}
      onClick={() => exportMutation.mutate()}
    >
      {exportMutation.isPending ? "Exporting..." : "Export CSV"}
    </Button>
  );

  const filters = (
    <Paper
      component="section"
      aria-label="Audit filters"
      variant="outlined"
      sx={{ p: 2 }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            xl: "repeat(6, minmax(0, 1fr))",
          },
          gap: 1.5,
        }}
      >
        <TextField
          size="small"
          label="Actor"
          placeholder="Filter by actor..."
          value={actorFilter}
          onChange={(event) => {
            setActorFilter(event.target.value);
            setOffset(0);
          }}
        />
        <TextField
          size="small"
          label="Action"
          placeholder="Filter by action..."
          value={actionFilter}
          onChange={(event) => {
            setActionFilter(event.target.value);
            setOffset(0);
          }}
        />
        <TextField
          size="small"
          label="Resource type"
          placeholder="Filter by resource type..."
          value={resourceFilter}
          onChange={(event) => {
            setResourceFilter(event.target.value);
            setOffset(0);
          }}
        />
        <TextField
          size="small"
          label="Resource ID"
          placeholder="Filter by exact resource id..."
          value={resourceIdFilter}
          onChange={(event) => {
            setResourceIdFilter(event.target.value);
            setOffset(0);
          }}
        />
        <TextField
          size="small"
          type="date"
          label="From date"
          value={fromDate}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(event) => {
            setFromDate(event.target.value);
            setOffset(0);
          }}
        />
        <TextField
          size="small"
          type="date"
          label="To date"
          value={toDate}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(event) => {
            setToDate(event.target.value);
            setOffset(0);
          }}
        />
      </Box>
    </Paper>
  );

  return (
    <PlatformPage title="Audit Log" actions={controls}>
      <Stack spacing={2}>
        {filters}

        {exportMutation.isError && (
          <Alert severity="error" role="alert">
            Couldn’t export the audit log. Check your connection and try again.
          </Alert>
        )}

        {isError ? (
          <Alert
            severity="error"
            role="alert"
            action={
              <Button color="inherit" size="small" onClick={() => refetch()}>
                Try again
              </Button>
            }
          >
            Couldn’t load the audit log. Adjust the filters or try again.
          </Alert>
        ) : isLoading ? (
          <Box
            role="status"
            aria-label="Loading Audit Log"
            aria-live="polite"
            sx={{ display: "flex", justifyContent: "center", py: 8 }}
          >
            <CircularProgress size={36} />
          </Box>
        ) : entries.length === 0 ? (
          <Box
            role="status"
            aria-live="polite"
            sx={{ py: 6, textAlign: "center" }}
          >
            <Typography color="text.secondary">
              No audit entries matched this investigation
            </Typography>
          </Box>
        ) : (
          <>
            <Typography color="text.secondary" variant="body2">
              {total.toLocaleString()} matching{" "}
              {total === 1 ? "entry" : "entries"}
            </Typography>

            <TableContainer
              component={Paper}
              variant="outlined"
              role="region"
              aria-label="Audit log results"
              tabIndex={0}
              sx={{
                overflowX: "auto",
                "&:focus-visible": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: 2,
                },
              }}
            >
              <Table
                size="small"
                aria-label="Audit log"
                sx={{ minWidth: 1120 }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Resource</TableCell>
                    <TableCell>Actor</TableCell>
                    <TableCell>IP address</TableCell>
                    <TableCell>Metadata</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((entry) => {
                    const href = resourceHref(
                      entry.resource_type,
                      entry.resource_id,
                    );
                    const metadata = entry.metadata
                      ? Object.entries(entry.metadata)
                      : [];

                    return (
                      <TableRow key={entry.entry_id} hover>
                        <TableCell
                          sx={{ whiteSpace: "nowrap", verticalAlign: "top" }}
                        >
                          {formatDateTime(entry.created_at)}
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 600, verticalAlign: "top" }}
                        >
                          {entry.action}
                        </TableCell>
                        <TableCell sx={{ minWidth: 220, verticalAlign: "top" }}>
                          <Stack
                            spacing={0.75}
                            sx={{ alignItems: "flex-start" }}
                          >
                            <Chip
                              label={entry.resource_type}
                              size="small"
                              variant="outlined"
                            />
                            {href ? (
                              <Button
                                component={Link}
                                to={href}
                                aria-label={`${entry.resource_type}:${entry.resource_id}`}
                                size="small"
                                variant="text"
                                sx={{
                                  fontFamily: "monospace",
                                  justifyContent: "flex-start",
                                  maxWidth: 210,
                                  overflowWrap: "anywhere",
                                  p: 0,
                                  textTransform: "none",
                                }}
                              >
                                {entry.resource_id}
                              </Button>
                            ) : (
                              <Typography
                                component="div"
                                variant="caption"
                                sx={{
                                  fontFamily: "monospace",
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {entry.resource_id}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ minWidth: 190, verticalAlign: "top" }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {getUserDisplayName(
                              entry.actor_display_name,
                              entry.actor_id,
                            )}
                          </Typography>
                          <Typography
                            color="text.secondary"
                            variant="caption"
                            sx={{
                              fontFamily: "monospace",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {entry.actor_id}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{ whiteSpace: "nowrap", verticalAlign: "top" }}
                        >
                          {entry.ip_address || "Not captured"}
                        </TableCell>
                        <TableCell sx={{ minWidth: 240, verticalAlign: "top" }}>
                          {metadata.length > 0 ? (
                            <Box component="dl" sx={{ m: 0 }}>
                              {metadata.map(([key, value]) => (
                                <Box
                                  key={key}
                                  component="div"
                                  sx={{ mb: 0.75 }}
                                >
                                  <Typography
                                    component="dt"
                                    color="text.secondary"
                                    variant="caption"
                                  >
                                    {formatMetadataLabel(key)}
                                  </Typography>
                                  <Typography
                                    component="dd"
                                    variant="caption"
                                    sx={{
                                      fontFamily: "monospace",
                                      m: 0,
                                      overflowWrap: "anywhere",
                                    }}
                                  >
                                    {formatMetadataValue(value)}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography color="text.secondary" variant="body2">
                              None
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {total > PAGE_SIZE && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  sx={{ alignItems: "center", justifyContent: "space-between" }}
                >
                  <Typography color="text.secondary" variant="body2">
                    Page {page} of {pageCount}
                  </Typography>
                  <Pagination
                    page={page}
                    count={pageCount}
                    size="small"
                    getItemAriaLabel={(type, itemPage) =>
                      paginationLabel(type, itemPage)
                    }
                    onChange={(_event, nextPage) =>
                      setOffset((nextPage - 1) * PAGE_SIZE)
                    }
                  />
                </Stack>
              </Paper>
            )}
          </>
        )}
      </Stack>
    </PlatformPage>
  );
}
